/* This is the Root component mainly initializes Redux and React Router. */
import React, { useEffect, useRef, useState } from 'react';
import { Router, Route, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';
import axios from 'axios';
import eurekaMgrs from '@eureka/ui-managers';
import eureka from 'eureka';
import { ThemeProvider, MessageStrip, MessageStripDesign } from '@ui5/webcomponents-react';
import { setLanguage as setUi5Language } from '@ui5/webcomponents-base/dist/config/Language.js';
import { setTheme } from '@ui5/webcomponents-base/dist/config/Theme.js';
import '@ui5/webcomponents/dist/features/InputSuggestions.js';
import '@ui5/webcomponents-icons/dist/Assets-static';
import '@eureka/ui-managers/src/styles/layout.css';

import { listenToEventBus, renderRouteConfigV3, addScriptToBody } from './App.helper';
import history from './common/history';
import routeConfig from './common/routeConfig';
import store from './common/store';
import { getURLParam, TestingLocales } from './common/Utils';
import { MicroFrontend } from './features/common';
import { getRandom } from './features/common/Utils';
import { setDocumentLang } from './common/Utils';
// import WebAssistant from './common/web-assistant/WebAssistant';

// const { MessageToast } = eureka.controls;
const { initI18n, setLanguage, getUi5Language } = eureka.I18nProvider;
const { Spinner } = eureka.components;
const { addConfig, getConfig, setCsrfToken, updateFeatureToggle } = eurekaMgrs.ConfigManager;
const configManagerSetLanguage = eurekaMgrs.ConfigManager.setLanguage;
const configManagerGetLanguage = eurekaMgrs.ConfigManager.getLanguage;
const { getDefaultThemeId, getThemeId, setThemeId } = eurekaMgrs.AppearanceManager;

let lng = 'en-US';
let themeId = getDefaultThemeId();
const testingLng = getURLParam(window.location.search, 'sap-language');
const testingThemeId = getURLParam(window.location.search, 'sap-ui-theme');

// const getDataFromResults = ({ results, index, defValue = {} }) => {
//   return Array.isArray(results) && results.length > index && results[index]?.data
//     ? results[index]?.data
//     : defValue;
// };

const onFetchConfigSuccess = ({ manifest, setState, setMicroFrontends }) => {
  const shell = eureka.I18nProvider.getLocation(manifest, manifest['shell-ui']);
  const microFrontends = [];
  manifest.components.forEach((component) => {
    const host = eureka.I18nProvider.getLocation(manifest, component);
    microFrontends.push({
      name: component.config.app,
      host,
      routers: component.config.routers,
    });
  });
  setState((prevState) => ({
    ...prevState,
    config: manifest,
  }));
  setMicroFrontends(microFrontends);
  addConfig('appConfig', manifest);
  // i18next configuration: https://www.i18next.com/overview/configuration-options
  initI18n(
    {
      shell,
    },
    {
      debug: process.env.NODE_ENV === 'production',
      lowerCaseLng: false,
      fallbackLng: 'en-US',
      fallbackNS: 'shell',
      whitelist: false,
      lng, // en-US en-US-sappsd
      load: 'currentOnly',
      defaultNS: 'shell',
      ns: 'shell',
      preload: [lng], // en-US en-US-sappsd
      react: {
        useSuspense: false,
        wait: false,
      },
    },
  );
  // Handle error page
  if (window.location.pathname.startsWith('/error')) {
    setState((prevState) => ({
      ...prevState,
      initializing: false,
    }));
  }
};

const onFetchConfigFailed = ({ error, setState }) => {
  setState((prevState) => ({
    ...prevState,
    // initializing: false,
    fetchConfigError: error,
  }));
};

const onFetchAuthSuccess = ({ auth, user }) => {
  window.hasLoggedin = true;
  if (auth?.data) {
    addConfig('user', auth?.data);
  }
  user.current = auth?.data;
};

export const onFetchAuthFailed = ({ error, setState, mmp }) => {
  if (window.location.href.indexOf('/login') < 0 && error.request.status === 401) {
    // TODO:
    window.location.href = `/login?application=${mmp}`;
  } else if (window.location.href.indexOf('/login') < 0 && error.request.status !== 401) {
    window.hasLoggedin = false;
    setState((prevState) => ({
      ...prevState,
      authUserError: error,
    }));
  } else {
    window.hasLoggedin = false;
    console.log(`Auth user error: ${error}`);
    setState((prevState) => ({
      ...prevState,
      initializing: false,
    }));
  }
};

const onFetchCsrfSuccess = ({ csrf, setCsrfToken }) => {
  setCsrfToken(csrf?.data?.token);
};

const onFetchCsrfFailed = ({ error, setState, user, setCsrfToken }) => {
  // set a fake csrf token
  setCsrfToken('fakecsrftoken');
  setState((prevState) => ({
    ...prevState,
    settings: {},
    user: user.current,
  }));
};

export const onFetchSettingsFinally = ({ setState }) => {
  if (testingLng) {
    lng = TestingLocales[testingLng] ? TestingLocales[testingLng] : testingLng;
  }
  if (testingThemeId) {
    themeId = testingThemeId;
  }
  // set fiori theme
  setThemeId(themeId);
  setTheme(getThemeId());
  // set new lng
  setLanguage(lng);
  configManagerSetLanguage(lng);
  setUi5Language(getUi5Language(lng));
  setDocumentLang(document, lng);
  // set initialization done
  setState((prevState) => ({
    ...prevState,
    initializing: false,
  }));
};

const isPromiseFulfilledResult = (res) =>
  Reflect.has(res, 'status') && res.status === 'fulfilled' && Reflect.has(res, 'value');

const isPromiseRejectedResult = (res) =>
  Reflect.has(res, 'status') && res.status === 'rejected' && Reflect.has(res, 'reason');

const processSettledResult = (result, successCb, failedCb) => {
  if (isPromiseFulfilledResult(result)) {
    successCb && successCb(result.value);
  } else if (isPromiseRejectedResult(result)) {
    failedCb && failedCb(result.reason);
    throw result.reason;
  }
};

const appInitDefaultProcess = async ({
  fetchAuth,
  fetchCsrf,
  user,
  rawSettings,
  setState,
  setCsrfToken,
  config,
}) => {
  const [authRes, csrfRes] = await Promise.allSettled([fetchAuth(), fetchCsrf()]);
  try {
    processSettledResult(
      authRes,
      (value) => onFetchAuthSuccess({ auth: value, user }),
      (error) => onFetchAuthFailed({ error, setState, mmp: config.mmp }),
    );
    processSettledResult(
      csrfRes,
      (value) => onFetchCsrfSuccess({ csrf: value, setCsrfToken }),
      (error) => onFetchCsrfFailed({ error, setState, user, setCsrfToken }),
    );
  } catch (error) {
    console.error(error.message);
  }
  onFetchSettingsFinally({ rawSettings, setState });
};

const onCustomFetchConfigSuccess = async ({
  config,
  microFrontends,
  // state,
  setState,
  setMicroFrontends,
}) => {
  addConfig('appConfig', config);
  setState((prevState) => ({
    ...prevState,
    config,
  }));
  setMicroFrontends(microFrontends);
  // Handle error page
  if (window.location.pathname.startsWith('/error')) {
    setState((prevState) => ({
      ...prevState,
      initializing: false,
    }));
  }
};

const onFetchSettingsFailed = ({ setState, user }) => {
  setState((prevState) => ({
    ...prevState,
    settings: {},
    user: user.current,
    fetchConfigError: false,
  }));
};

const getShellLib = (config = {}) => {
  const shellLibConfig = config.components?.find((component) => {
    return component?.name.indexOf('-shell-lib') > -1;
  });
  if (shellLibConfig) {
    const regex = /^(\w+)-shell-lib/;
    const match = shellLibConfig?.name.match(regex);
    return { location: shellLibConfig.location, name: match[1] };
  }
  return {};
};

const onAppInit = async ({
  fetchConfig,
  fetchAuth,
  fetchCsrf,
  user,
  rawSettings,
  setState,
  setMicroFrontends,
  setCsrfToken,
}) => {
  let config;
  let settings = { basicSetup: {}, userProfile: {}, companyProfile: {} };

  try {
    const configRes = await fetchConfig();
    config = configRes.data;
    config.components = config?.components?.filter((c) => c.name.indexOf('shell-ui') === -1);
    const { location, name } = getShellLib(config);
    config.mmp = name;
    config.shellLibLocation = location;

    if (!config.shellLibLocation) {
      onFetchConfigSuccess({
        manifest: configRes?.data,
        setState,
        setMicroFrontends,
      });
      await appInitDefaultProcess({
        fetchAuth,
        fetchCsrf,
        user,
        rawSettings,
        setState,
        setCsrfToken,
        config,
      });
      return;
    }
  } catch (error) {
    console.error('Fetch config.json failed.');
    onFetchConfigFailed({ error, setState });
    throw error;
  }

  if (config.shellLibLocation) {
    // TODO: mmp custom init process
    const manifestUrl = `${config.shellLibLocation}/asset-manifest.json`;
    const manifest = await axios.get(manifestUrl);
    const shellLibUrl = manifest.data.files['shell-lib.js'];
    // const res = await axios.get(`${shellLibLocation}${shellLibUrl}`);
    // (0, eval)(res.data);

    await addScriptToBody({ src: `${config.shellLibLocation}${shellLibUrl}` });

    const shellLib = window.__ShellLib__.default;

    await shellLib.onInit({ eureka });

    try {
      const res = await shellLib.onFetchConfigSuccess({ manifest: config });
      onCustomFetchConfigSuccess({
        config: res?.config || config,
        microFrontends: res?.microFrontends || [],
        manifest,
        setState,
        setMicroFrontends,
      });
    } catch (error) {
      throw error;
    }

    try {
      const authRes = await fetchAuth();
      // TODO: onFetchAuthSuccess 内的逻辑放在哪边
      const res = await shellLib.onFetchAuthSuccess(authRes.data);
      onFetchAuthSuccess({ auth: authRes.data, user });

      try {
        const csrfRes = await fetchCsrf();
        // TODO:
        const res = await shellLib.onFetchCsrfSuccess(csrfRes.data);
        onFetchCsrfSuccess({ csrf: csrfRes });
      } catch (error) {
        // TODO:
        await shellLib.onFetchCsrfFailed(error);
        onFetchCsrfFailed({ error, user, setState, setCsrfToken });
      }

      try {
        const res = await shellLib.onFetchSettings({});
        settings = res?.settings || {};
        const userInfo = res?.userInfo || {};
        if (res?.rawSettings) {
          rawSettings.current = res.rawSettings;
        }
        user.current = userInfo;
        user.current.databaseUserId = rawSettings.current.basicSetup?.id;
        setState((prevState) => ({
          ...prevState,
          settings,
          ...(rawSettings.current.userProfile ? { user: rawSettings.current.userProfile } : null),
        }));
      } catch (error) {
        console.error(error);
        onFetchSettingsFailed({ setState, user });
      } finally {
        onFetchSettingsFinally({ rawSettings, setState });
      }
    } catch (error) {
      // TODO: onFetchAuthFailed 内的逻辑放在哪边
      await shellLib.onFetchAuthFailed(error);
      onFetchAuthFailed({ error, setState, mmp: config.mmp });
      throw error;
    }
  }
};

export const loader = () => {
  return <div>Loading...</div>;
};

export const renderInitializing = () => {
  return (
    <div
      className="app-loading"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Spinner cssClass="page-load-spinner" />
    </div>
  );
};

export const renderError = (msg) => {
  return (
    <MessageStrip
      style={{ marginTop: '10px', marginRight: '10px' }}
      type={MessageStripDesign.Negative}
      icon="error"
      noIcon={false}
      noCloseButton
      onClose={() => {}}
    >
      {msg}
    </MessageStrip>
  );
};

export const MicroFrontendWrapper = ({ history, match, host, name, config, settings, user }) => {
  if (!settings) {
    console.error('Settings for microfrontends is empty, which is not allowed');
    return null;
  }
  return (
    <MicroFrontend
      history={history}
      match={match}
      host={host}
      name={name}
      config={config}
      settings={settings}
      user={user.current}
      eventBus={eventBus}
    />
  );
};

export const renderMicroFrontendRoutes = ({ mfdRouters, history, config, settings, user }) => {
  const routes = [];
  mfdRouters.forEach((app) => {
    app.routers.forEach((route) => {
      routes.push(
        <Route
          key={route + getRandom()}
          exact
          path={route}
          component={(props) => (
            <MicroFrontendWrapper
              {...props}
              host={app.host}
              name={app.name}
              history={history}
              config={config}
              settings={settings}
              user={user}
            />
          )}
        />,
      );
    });
  });
  return routes;
};

export const renderMfes = ({ state, user, microFrontends }) => {
  const { config, settings } = state;
  const containerRoutes = renderRouteConfigV3({
    routes: routeConfig,
    contextPath: '/',
    config,
    settings,
    user,
  });
  let routes = [];
  if (!config.shellLibLocation) {
    const microFrontendRoutes = renderMicroFrontendRoutes({
      mfdRouters: microFrontends,
      history,
      config,
      settings,
      user,
    });
    routes = [...microFrontendRoutes, ...containerRoutes];
  } else {
    const shellLib = window.__ShellLib__.default;
    if (!shellLib) {
      throw new Error('Cannot find ShellLib on window when rendering microfrontends.');
    }
    const renderRouteConfigWrapper = (props) =>
      renderRouteConfigV3({
        contextPath: '/',
        config,
        settings,
        user,
        ...props,
      });
    const renderMicroFrontendRoutesWrapper = (props) => {
      return renderMicroFrontendRoutes({ history, config, settings, user, ...props });
    };
    const allRoutes = shellLib.onBeforeRender({
      renderRouteConfig: renderRouteConfigWrapper,
      renderMicroFrontendRoutes: renderMicroFrontendRoutesWrapper,
    });
    routes = [...allRoutes, ...containerRoutes];
  }

  console.log(123456, routes);

  return (
    <ThemeProvider>
      <Provider store={store}>
        <Router history={history}>
          {/* <WebAssistant lang={configManagerGetLanguage() || 'en-US'} /> */}
          <Switch multiple={5}>{routes}</Switch>
        </Router>
      </Provider>
    </ThemeProvider>
  );
};

const App = ({ fetchConfig, fetchAuth, fetchCsrf }) => {
  const [state, setState] = useState({
    initializing: true,
    fetchConfigError: false,
    fetchSettingsError: false,
    authUserError: false,
    config: null,
    settings: { basicSetup: {}, userProfile: {}, companyProfile: {} },
    user: {},
  });
  const [microFrontends, setMicroFrontends] = useState([]);
  const user = useRef(null);
  const rawSettings = useRef({ basicSetup: {}, userProfile: {}, companyProfile: {} });

  useEffect(() => {
    // addConfig('application', 'rgp');
    onAppInit({
      fetchAuth,
      fetchConfig,
      fetchCsrf,
      rawSettings,
      user,
      setState,
      setMicroFrontends,
      setCsrfToken,
    });
  }, []);

  useEffect(() => {
    // console.log('rawSeetings', history);
    listenToEventBus({ rawSettings, setState, history });
    // console.log('State Updated', state);
  }, []);

  if (state.fetchConfigError) {
    return renderError('Failed to load config, please try again.', state.fetchConfigError);
  }

  if (state.authUserError) {
    return renderError('Failed to get user information, please refresh page.', state.authUserError);
  }

  if (state.fetchSettingsError) {
    return renderError(
      'Failed to get company or user settings, please refresh page.',
      state.fetchSettingsError,
    );
  }

  if (state.initializing) {
    return renderInitializing();
  }

  return renderMfes({ state, user, microFrontends });
};

export default App;
